import Icon from 'jitsi/base/icons/components/Icon';
import { IconArrowLeft, IconArrowRight } from 'jitsi/base/icons/svg';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
const useStyles = makeStyles()((theme, { py, px, p = 0, gap = 2 }, classes) => {
    const buttonsWidth = 30;
    const buttonsMargin = theme.spacing(1);
    return {
        container: {
            position: 'relative',
            width: '100%',
        },
        slider: {
            display: 'flex',
            width: '100%',
            scrollBehavior: 'smooth',
            overflowX: 'scroll',
            scrollbarWidth: 'none',
            padding: `${theme.spacing(py || p)} 0`,
            '&::-webkit-scrollbar': {
                display: 'none',
            },
        },
        buttonGhost: {
            position: 'absolute',
            top: 0,
            zIndex: 2,
            height: '100%',
        },
        backButtonGhost: {
            left: 0,
            background: 'white',
        },
        forwardButtonGhost: {
            right: 0,
            background: 'white',
        },
        slide: {
            minWidth: 'max-content',
            width: 'max-content',
            marginLeft: theme.spacing(gap),
            '&:first-of-type': {
                marginLeft: theme.spacing(px || p),
            },
            '&:last-of-type': {
                marginRight: theme.spacing(px || p),
            },
        },
        button: {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            borderRadius: '50%',
            backgroundColor: theme.palette.iisightWhiteBackground,
            boxShadow: '0px 3px 10px 4px rgba(0, 0, 0, .63)',
            zIndex: 3,
            width: buttonsWidth,
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            '& svg, & path': {
                fill: theme.palette.iisightIcon,
            },
        },
        backButton: {
            left: buttonsMargin,
        },
        forwardButton: {
            right: buttonsMargin,
        },
    };
});
const inRange = (val1, val2, by) => {
    return Math.abs(val2 - val1) < by;
};
export const Carousel = ({ children, gap, py, px, p }) => {
    const containerRef = useRef();
    const { t } = useTranslation();
    const { classes, cx } = useStyles({ gap, py, px, p });
    const [visibleArrows, setVisibleArrows] = useState({ back: false, front: false });
    const onScroll = useCallback((e) => {
        if (!containerRef.current)
            return;
        const { scrollWidth, scrollLeft, clientWidth } = e?.currentTarget || containerRef.current;
        const visibleArrows = { back: false, front: false };
        if (scrollLeft > 0)
            visibleArrows.back = true;
        if (scrollWidth > clientWidth && !inRange(scrollLeft, scrollWidth - clientWidth, 2))
            visibleArrows.front = true;
        setVisibleArrows(visibleArrows);
    }, []);
    useLayoutEffect(() => {
        onScroll();
    }, [children]);
    const onArrowClicked = useCallback((direction) => {
        const container = containerRef.current;
        if (!container)
            return;
        const multiplier = direction === 'forward' ? 1 : -1;
        const baseMovement = (container.clientWidth - 30) * multiplier;
        const maxScroll = direction === 'backward' ? 0 : container.scrollWidth - container.clientWidth;
        const withinSkipMargin = inRange(container.scrollLeft + baseMovement, maxScroll, 10);
        const skipFactor = withinSkipMargin ? 200 : 0;
        container.scrollBy({ left: baseMovement + skipFactor * multiplier });
    }, []);
    return (React.createElement("div", { className: classes.container },
        visibleArrows.back && (React.createElement(React.Fragment, null,
            React.createElement("span", { "aria-hidden": true, className: cx(classes.buttonGhost, classes.backButtonGhost) }),
            React.createElement("button", { type: 'button', className: cx(classes.button, classes.backButton), onClick: () => onArrowClicked('backward'), "aria-label": t('carousel.backwards') },
                React.createElement(Icon, { colorInClass: true, src: IconArrowLeft, size: 24 })))),
        React.createElement("div", { ref: containerRef, onScroll: onScroll, className: classes.slider, "aria-live": 'polite', "aria-label": 'slider', role: 'listbox', tabIndex: 0 }, children.map((child, index) => (React.createElement("div", { className: classes.slide, tabIndex: index, role: 'option', "aria-label": 'slide' }, child)))),
        visibleArrows.front && (React.createElement(React.Fragment, null,
            React.createElement("span", { "aria-hidden": true, className: cx(classes.buttonGhost, classes.forwardButtonGhost) }),
            React.createElement("button", { type: 'button', className: cx(classes.button, classes.forwardButton), onClick: () => onArrowClicked('forward'), "aria-label": t('carousel.forwards') },
                React.createElement(Icon, { src: IconArrowRight, size: 24 }))))));
};
